import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EntityLink } from "./_links";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import {
    Card as RebaseCard,
    Chip as RebaseChip,
    Separator as RebaseSeparator,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    ExternalLink as RebaseExternalLinkIcon,
    Globe as RebaseGlobeIcon,
    MapPin as RebaseMapPinIcon
} from "lucide-react";

import { ChipList, Fact, coverRootCls, textList } from "./_cover";
import { sinceLabel } from "./_prospects_shared";
import { publicJobUrl } from "../public_site";
import {
    JOB_STATUS_LABEL, JOB_STATUS_COLOR, WORK_MODEL_LABEL, EMPLOYMENT_LABEL,
    CONTRACT_LABEL, SENIORITY_LABEL, APPLICATION_METHOD_LABEL, JOB_SOURCE_LABEL
} from "../job_labels";
import { credentialLabel } from "../taxonomy/credentials";
import { jobFunctionLabel } from "../taxonomy/job-functions";
import { SubmissionReview } from "./SubmissionReview";
import {
    calendarDay,
    countLabel,
    daysLive,
    shortDayLabel,
    trafficSinceLabel
} from "../vacancy_figures";

const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const GlobeIcon = RebaseGlobeIcon as React.ComponentType<any>;
const MapPinIcon = RebaseMapPinIcon as React.ComponentType<any>;

/**
 * A vacancy's front cover.
 *
 * Same reasoning as the candidate's — see `TalentOverview` — applied to the
 * other half of the marketplace. A vacancy opened on its form: eighty-odd fields
 * in declaration order, of which a reader wants about twelve, and the one thing
 * that decides what to do about it (somebody applied and is waiting) was on a
 * different tab.
 *
 * ── What it will not say ────────────────────────────────────────────
 *
 * No candidate is named here except one who applied to this vacancy. Pairing a
 * posting with people who did not apply is the deleted matching feature, and it
 * stays deleted. The applicants section is `talent_applications` for this job
 * and nothing else.
 *
 * ── The one action ──────────────────────────────────────────────────
 *
 * Almost none. Everything a vacancy needs — answering an applicant, editing the
 * posting — is an act with its own surface: "Postulaciones", the form. A second
 * set of buttons here would be a second place to do the same thing, differently.
 * So this reads, and says where the doing happens.
 *
 * The exception is a vacancy a company sent us (`source = 'submission'`). There
 * the decision is an answer to somebody waiting, and the answer is an email:
 * "Publicar" and "Devolver con nota" (`SubmissionReview`) say what the company
 * will receive, and the history below them says what it actually did receive.
 * The status dropdown in the form still works and still emails — the backend
 * hooks the write, not the button — but it does not tell you that it will.
 */

const APPLICANT_LABEL: Record<string, string> = {
    applied: "Sin revisar",
    reviewing: "En revisión",
    interview: "Entrevista",
    rejected: "Descartado",
    hired: "Contratado"
};

const APPLICANT_COLOR: Record<string, string> = {
    applied: "blueLighter",
    reviewing: "orangeLighter",
    interview: "purpleLighter",
    rejected: "redLighter",
    hired: "greenLighter"
};

/** Still waiting on a decision from us. */
const OPEN_STATUSES = ["applied", "reviewing", "interview"];

/** How many applicants the cover names before it stops being a summary. */
const APPLICANTS_SHOWN = 5;

/** `job_traffic` for this vacancy: two counters, since 2026-09-21. */
interface TrafficRow {
    views: number;
    apply_clicks: number;
}

interface ApplicantRow {
    id: string;
    status?: string | null;
    created_at?: string | null;
    name: string;
}

/**
 * The deadline that actually retires a vacancy.
 *
 * `LEAST(expires_at, closing_date)`, which is what `retire-stale-jobs` reads —
 * and today that is `closing_date` alone, because the scraper's extraction has
 * never filled `expires_at` on any row. Reading only one of them would put this
 * screen and the cron on different definitions of "vencida".
 */
function deadlineOf(values: Record<string, any>): string | null {
    const dates = [values.expires_at, values.closing_date]
        .map(v => (v ? String(v) : null))
        .filter(Boolean) as string[];
    if (dates.length === 0) return null;
    return dates.sort()[0];
}

function isPast(iso: string | null): boolean {
    if (!iso) return false;
    const when = new Date(iso).getTime();
    return Number.isFinite(when) && when < Date.now();
}

export function JobOverview({ entity, modifiedValues, formContext }: EntityCustomViewParams) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;

    const jobId = entity?.id ? String(entity.id) : null;
    const values = (modifiedValues ?? entity?.values ?? {}) as Record<string, any>;

    const [applicants, setApplicants] = useState<ApplicantRow[] | null>(null);
    const [applicantsFailed, setApplicantsFailed] = useState(false);

    /**
     * Which vacancy the last request was for.
     *
     * A tab panel is keyed by the view, not by the record, so a host that swaps
     * the record underneath it — a side panel walked from one row to the next —
     * keeps this instance and its state. Without the guard, a late answer files
     * one vacancy's applicants under another's name.
     */
    const wanted = useRef<string | null>(null);

    useEffect(() => {
        setApplicants(null);
        setApplicantsFailed(false);
    }, [jobId]);

    /**
     * Who applied, named.
     *
     * `include: ["*"]` brings the talent row with each application, which is the
     * same one request the applicants panel makes — a lookup per applicant would
     * be twenty requests to print twenty names.
     */
    const load = useCallback(async () => {
        if (!data || !jobId) return;
        wanted.current = jobId;
        try {
            const page = await data.collection("talent-applications").find({
                where: { job_id: ["==", jobId] },
                orderBy: ["created_at", "desc"],
                include: ["*"],
                limit: 200
            });
            if (wanted.current !== jobId) return;
            const rows = (page?.data ?? []) as Record<string, any>[];
            setApplicants(rows.map(row => {
                // An included relation arrives wrapped: a reference around an
                // Entity around the row. A bare uuid means nothing included it.
                const ref = row.talent_id;
                const talent = ref && typeof ref === "object" ? (ref.data?.values ?? ref.data ?? ref) : null;
                return {
                    id: String(row.id),
                    status: row.status,
                    created_at: row.created_at,
                    name: (talent?.full_name ?? "").trim()
                        || (talent?.email ?? "").trim()
                        || "(candidato sin nombre)"
                };
            }));
            setApplicantsFailed(false);
        } catch {
            if (wanted.current !== jobId) return;
            setApplicants(null);
            setApplicantsFailed(true);
        }
    }, [data, jobId]);

    useEffect(() => { void load(); }, [load]);

    /**
     * What it does on the board — the same two counters the employer's
     * dashboard shows, from the same table. `undefined` while loading, `null`
     * when the read failed: a failed read is never printed as zero views. No
     * row at all is a real zero — `job_traffic` gets one on the first visit.
     */
    const [traffic, setTraffic] = useState<TrafficRow | null | undefined>(undefined);
    const wantedTraffic = useRef<string | null>(null);
    useEffect(() => {
        setTraffic(undefined);
        if (!data || !jobId) return;
        wantedTraffic.current = jobId;
        (async () => {
            try {
                const page = await data.collection("job-traffic").find({
                    where: { job_id: ["==", jobId] },
                    limit: 1
                });
                if (wantedTraffic.current !== jobId) return;
                const row = (page?.data ?? [])[0] as Record<string, any> | undefined;
                setTraffic({ views: Number(row?.views ?? 0), apply_clicks: Number(row?.apply_clicks ?? 0) });
            } catch {
                if (wantedTraffic.current !== jobId) return;
                setTraffic(null);
            }
        })();
    }, [data, jobId]);

    const open = useMemo(
        () => (applicants ?? []).filter(a => OPEN_STATUSES.includes(String(a.status ?? "applied"))),
        [applicants]
    );

    const status = String(values.status ?? "draft");
    const deadline = deadlineOf(values);
    const expired = isPast(deadline) && status !== "closed";

    /**
     * What this vacancy needs, in one sentence.
     *
     * Ordered by who is waiting, exactly as the candidate queue is: an applicant
     * refreshing an inbox outranks a posting that merely sits there. The rest
     * are states worth knowing before you read the fields — a vacancy past its
     * deadline and still open is on the board saying something untrue.
     */
    const next: string | null = (() => {
        // Nothing until the applicants are known. Every branch below turns on
        // whether somebody is waiting, so answering before the query lands means
        // printing "publicada y sin candidaturas todavía" over a vacancy with
        // three people waiting — on the one line of this page that is supposed
        // to say what to do next.
        if (applicants === null) return null;
        if (open.length > 0) {
            return open.length === 1
                ? "Una persona se postuló y sigue esperando respuesta. Está en «Postulaciones»."
                : `${open.length} personas se postularon y siguen esperando respuesta. Están en «Postulaciones».`;
        }
        if (expired) return "Pasó su fecha de cierre y sigue abierta. El cron la retira; si corre prisa, ciérrala en la ficha.";
        if (status === "pending_review" && values.source === "submission") {
            return "La envió una empresa y espera respuesta: publícala o devuélvesela con una nota. Nadie la ve en el tablón todavía.";
        }
        if (status === "pending_review") return "Está esperando revisión: nadie la ve en el tablón todavía.";
        if (status === "returned") {
            return `Se la devolvimos a la empresa ${sinceLabel(values.returned_at)} y está esperando que la corrija. ` +
                "Cuando la reenvíe, vuelve a «Pendiente de revisión».";
        }
        if (status === "draft") return "Es un borrador. No está publicada.";
        if (status === "closed") return null;
        if (values.application_method && values.application_method !== "internal") {
            return "Publicada. El botón de postular lleva fuera, así que aquí no habrá candidaturas.";
        }
        return "Publicada y sin candidaturas todavía.";
    })();

    const salary = (() => {
        const display = (values.salary_display ?? "").toString().trim();
        if (display) return display;
        const min = values.salary_min;
        const max = values.salary_max;
        if (!min && !max) return null;
        const range = [min, max].filter(Boolean).join(" – ");
        const currency = values.salary_currency ? ` ${values.salary_currency}` : "";
        const period = values.salary_period === "annual" ? "/año"
            : values.salary_period === "monthly" ? "/mes"
                : values.salary_period === "hourly" ? "/hora" : "";
        return `${range}${currency}${period}`;
    })();

    const languages = Array.isArray(values.required_languages)
        ? values.required_languages
            .map((l: any) => {
                if (typeof l === "string") return l;
                const language = (l?.language ?? l?.name ?? "").toString().trim();
                const level = (l?.level ?? "").toString().trim();
                if (!language) return null;
                return level ? `${language} · ${level}` : language;
            })
            .filter(Boolean) as string[]
        : [];

    // Canonical ids where the normalizer reached them, printed through the
    // catalog so "iso_14001" reads as the credential and not as a key.
    // `credentialLabel` returns the id itself for anything it does not know, so
    // an employer's own wording survives rather than being dropped.
    const requiredCreds = textList(values.required_certifications_canon, values.required_certifications)
        .map(credentialLabel);
    const requiredSkills = textList(values.required_skills_canon, values.required_skills)
        .map(credentialLabel);
    const desiredCreds = textList(values.desired_certifications_canon, values.desired_certifications)
        .map(credentialLabel);
    const desiredSkills = textList(values.desired_skills_canon, values.desired_skills)
        .map(credentialLabel);

    if (!jobId) {
        return (
            <div className="p-6">
                <Typography variant="body1" color="secondary">
                    Guarda la vacante primero. Esta portada resume lo que ya está en la base —
                    las candidaturas se archivan contra una vacante que existe.
                </Typography>
            </div>
        );
    }

    return (
        <div className={coverRootCls}>
            {/* ── What this is ────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <Typography variant="h5" component="h2" className="!mb-0">
                        {(values.title ?? "").toString().trim() || "(vacante sin título)"}
                    </Typography>
                    <Chip size="small" colorScheme={JOB_STATUS_COLOR[status] ?? "grayLighter"}>
                        {JOB_STATUS_LABEL[status] ?? status}
                    </Chip>
                    {values.featured && <Chip size="small" colorScheme="yellowLighter">Destacada</Chip>}
                    {values.is_verified && <Chip size="small" colorScheme="blueLighter">Verificada</Chip>}
                </div>

                <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                    {values.company_name && (
                        <Typography variant="body2" className="font-medium">
                            <EntityLink kind="company" id={values.company_id}>{values.company_name}</EntityLink>
                        </Typography>
                    )}
                    {(values.location_display || values.location_city) && (
                        <span className="inline-flex items-center gap-1.5 text-sm">
                            <MapPinIcon size={iconSize.smallest} className="opacity-60" />
                            {values.location_display || values.location_city}
                        </span>
                    )}
                    {values.work_model && (
                        <Typography variant="body2" color="secondary">
                            {WORK_MODEL_LABEL[String(values.work_model)] ?? String(values.work_model)}
                        </Typography>
                    )}
                    {/* Only when published: RLS exposes exactly those to an
                        anonymous reader, so a link to anything else is a
                        "no encontrada" page — the same rule the entity action
                        applies. */}
                    {status === "published" && (
                        <a href={publicJobUrl(jobId)} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                            ver en el tablón <ExternalLinkIcon size={12} />
                        </a>
                    )}
                    {values.source_url && (
                        <a href={values.source_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                            <GlobeIcon size={iconSize.smallest} /> origen
                        </a>
                    )}
                </div>

                <Typography variant="caption" color="secondary">
                    {[
                        values.posted_at ? `publicada ${sinceLabel(values.posted_at)}` : null,
                        deadline ? `cierra ${sinceLabel(deadline)}` : "sin fecha de cierre",
                        values.source ? JOB_SOURCE_LABEL[String(values.source)] ?? String(values.source) : null
                    ].filter(Boolean).join(" · ")}
                </Typography>
            </div>

            {/* ── What it does on the board ───────────────────────── */}
            {(status === "published" || status === "closed") && (
                <BoardFigures
                    traffic={traffic}
                    publishedAt={values.published_at}
                    live={status === "published"}
                    internal={!values.application_method || values.application_method === "internal"}
                    applicants={applicants?.length ?? null}
                    reminderSentAt={values.closing_reminder_sent_at}
                    posterEmail={values.poster_email}
                    hasClosingDate={Boolean(values.closing_date)}
                />
            )}

            {/* ── What it needs ───────────────────────────────────── */}
            {next && (
                <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typography variant="micro" color="secondary">Lo siguiente</Typography>
                        {open.length > 0 && (
                            <Chip size="small" colorScheme="redLighter">
                                {open.length === 1 ? "1 sin responder" : `${open.length} sin responder`}
                            </Chip>
                        )}
                        {expired && <Chip size="small" colorScheme="orangeLighter">vencida</Chip>}
                    </div>
                    <Typography variant="body2">{next}</Typography>
                </Card>
            )}

            {/* ── The company that sent it ────────────────────────── */}
            {values.source === "submission" && (
                <SubmissionReview
                    data={data}
                    jobId={jobId}
                    values={values}
                    formex={(formContext as any)?.formex}
                />
            )}

            <Separator />

            {/* ── What it asks for ────────────────────────────────── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">Qué pide</Typography>

                <div className="grid gap-x-6 gap-y-3 grid-cols-1 sm:grid-cols-2">
                    <Fact label="Tipo de trabajo">
                        {values.job_function ? jobFunctionLabel(String(values.job_function)) : null}
                    </Fact>
                    <Fact label="Nivel">
                        {values.seniority ? SENIORITY_LABEL[String(values.seniority)] ?? String(values.seniority) : null}
                    </Fact>
                    <Fact label="Experiencia">{values.experience_years}</Fact>
                    <Fact label="Jornada">
                        {values.employment_type ? EMPLOYMENT_LABEL[String(values.employment_type)] ?? String(values.employment_type) : null}
                    </Fact>
                    <Fact label="Contrato">
                        {values.contract_type ? CONTRACT_LABEL[String(values.contract_type)] ?? String(values.contract_type) : null}
                    </Fact>
                    <Fact label="Departamento">{values.department}</Fact>
                    <Fact label="Salario">
                        {salary
                            ? <>{salary}{values.show_salary === false ? " (oculto en el tablón)" : ""}</>
                            : null}
                    </Fact>
                    <Fact label="Idiomas">{languages.length ? languages.join(" · ") : null}</Fact>
                    <Fact label="Viajes">{values.travel_required ? "Sí" : null}</Fact>
                </div>

                {textList(values.required_studies).length > 0 && (
                    <Fact label="Estudios"><ChipList values={textList(values.required_studies)} /></Fact>
                )}
                {requiredCreds.length > 0 && (
                    <Fact label="Certificaciones requeridas"><ChipList values={requiredCreds} /></Fact>
                )}
                {requiredSkills.length > 0 && (
                    <Fact label="Habilidades requeridas"><ChipList values={requiredSkills} /></Fact>
                )}
                {(desiredCreds.length > 0 || desiredSkills.length > 0) && (
                    <Fact label="Deseables">
                        <ChipList values={[...desiredCreds, ...desiredSkills]} />
                    </Fact>
                )}

                <Fact label="Dónde se postula">
                    {values.application_method
                        ? APPLICATION_METHOD_LABEL[String(values.application_method)] ?? String(values.application_method)
                        : null}
                </Fact>
            </div>

            {values.summary && (
                <>
                    <Separator />
                    <div className="flex flex-col gap-2">
                        <Typography variant="subtitle2" component="h3">Resumen</Typography>
                        {/* The summary, not the description: the latter is
                            markdown measured in screens, and the form renders it
                            with an editor that can actually read it. */}
                        <Typography variant="body1" className="whitespace-pre-wrap break-words">
                            {String(values.summary)}
                        </Typography>
                    </div>
                </>
            )}

            <Separator />

            {/* ── Who applied ─────────────────────────────────────── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">
                    {applicants === null ? "Candidaturas" : `Candidaturas (${applicants.length})`}
                </Typography>
                {applicantsFailed && (
                    <Typography variant="body2" color="secondary">
                        No se pudieron leer las candidaturas.
                    </Typography>
                )}
                {applicants?.length === 0 && (
                    <Typography variant="body2" color="secondary">
                        {values.application_method && values.application_method !== "internal"
                            // Not a failing of the vacancy: 103 of 107 rows send
                            // the applicant to somebody else's site and were
                            // never going to produce a row here.
                            ? "Nadie se ha postulado aquí, y no lo hará: el botón de postular lleva fuera de SustenTalent."
                            : "Nadie se ha postulado todavía."}
                    </Typography>
                )}
                {applicants?.slice(0, APPLICANTS_SHOWN).map(applicant => {
                    const applicantStatus = String(applicant.status ?? "applied");
                    return (
                        <Card key={applicant.id} className="p-3 flex items-center gap-2 flex-wrap">
                            <Typography variant="body2" className="grow font-medium">
                                {applicant.name}
                            </Typography>
                            <Typography variant="body2" color="secondary">
                                se postuló {sinceLabel(applicant.created_at)}
                            </Typography>
                            <Chip size="small" colorScheme={APPLICANT_COLOR[applicantStatus] ?? "grayLighter"}>
                                {APPLICANT_LABEL[applicantStatus] ?? applicantStatus}
                            </Chip>
                        </Card>
                    );
                })}
                {applicants && applicants.length > APPLICANTS_SHOWN && (
                    <Typography variant="body2" color="secondary">
                        Y {applicants.length - APPLICANTS_SHOWN} más. La lista completa, con lo que cada
                        persona trae frente a lo que pide esta vacante, está en «Postulaciones».
                    </Typography>
                )}
            </div>
        </div>
    );
}

/**
 * Views, apply clicks, candidatures, time live and the closing reminder — the
 * employer's dashboard figures, so the team reads the same numbers the
 * employer does.
 *
 * Same rules as there: figures from before counting began carry "desde el
 * 21 sep"; an apply button that leads off the site gets no candidature count;
 * a failed read is "no se pudo leer", never zero.
 */
function BoardFigures({ traffic, publishedAt, live, internal, applicants, reminderSentAt, posterEmail, hasClosingDate }: {
    traffic: TrafficRow | null | undefined;
    publishedAt: unknown;
    live: boolean;
    internal: boolean;
    applicants: number | null;
    reminderSentAt: unknown;
    posterEmail: unknown;
    hasClosingDate: boolean;
}) {
    const published = publishedAt ? String(publishedAt) : null;
    const since = trafficSinceLabel(published);
    const days = daysLive(published);
    const publishedDay = calendarDay(published);

    const counted = traffic === undefined
        ? "…"
        : traffic === null
            ? "no se pudo leer"
            : `${countLabel(traffic.views, "vista", "vistas")} · ${countLabel(traffic.apply_clicks, "clic en postular", "clics en postular")}`;

    return (
        <div className="grid gap-x-6 gap-y-3 grid-cols-1 sm:grid-cols-2">
            <Fact label={since ? `Vistas y clics (${since})` : "Vistas y clics"}>{counted}</Fact>
            <Fact label="Candidaturas">
                {internal
                    ? (applicants === null ? "…" : countLabel(applicants, "candidatura", "candidaturas"))
                    : "llegan por el enlace de la oferta; aquí solo se ven los clics"}
            </Fact>
            {live && (
                // "En el tablón desde", not "Publicada": the caption above
                // already says "publicada" about `posted_at`, which is when the
                // employer sent it, and one word must not name two dates.
                <Fact label="En el tablón desde">
                    {publishedDay
                        ? `${days === 0 ? "hoy" : days === 1 ? "hace 1 día" : `hace ${days} días`} (${shortDayLabel(publishedDay, calendarDay(new Date()))})`
                        : "sin fecha: se publicó antes de que se registrara"}
                </Fact>
            )}
            {live && (
                <Fact label="Aviso de cierre al publicador">
                    {reminderSentAt
                        ? `enviado ${sinceLabel(String(reminderSentAt))}`
                        : !posterEmail
                            ? "no hay email del publicador, así que no se le avisa"
                            : !hasClosingDate
                                ? "sin fecha de cierre, así que no hay aviso"
                                : "todavía no: sale una semana antes del cierre"}
                </Fact>
            )}
        </div>
    );
}

export default JobOverview;
